import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Terminal } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("admin@example.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="hidden lg:flex relative grid-bg border-r border-[#262626] p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <Terminal size={22} weight="bold" className="text-[#0055FF]" />
          <span className="font-display font-black tracking-tight text-lg">DASHBOARD</span>
        </div>
        <div>
          <h1 className="font-display text-5xl font-black tracking-tight leading-none">
            Ship faster.<br/>
            <span className="text-[#0055FF]">Own your stack.</span>
          </h1>
          <p className="mt-6 text-[#A1A1AA] text-sm max-w-md leading-relaxed">
            A unified control plane for CRUD, uploads, payments, notifications and AI.
            Built for developers who want production-grade primitives without the busy-work.
          </p>
        </div>
        <div className="font-mono text-xs text-[#71717A]">
          v1.0.0 · gpt-5.2 · stripe · sendgrid · twilio
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Prefilled admin credentials for quick access.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-sm bg-[#141414] border-[#262626]"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-sm bg-[#141414] border-[#262626]"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white h-10"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <div className="text-sm text-[#A1A1AA] flex justify-between">
            <Link to="/register" className="hover:text-white" data-testid="link-register">Create account</Link>
            <Link to="/forgot-password" className="hover:text-white" data-testid="link-forgot">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
